import { ApolloError } from "@apollo/client";
import { Flex, Icon } from "@chakra-ui/react";
import React from "react";
import { HiOutlineSparkles } from "react-icons/hi2";

import { useSendGAEvent } from "../../../utils/googleAnalytics";
import { AiInterviewCallFragment, CallNoteFragment } from "../../graphql";
import SidebarTabs, { SidebarTab } from "../AiRecording/SidebarTabs";
import ClipsTab from "../AiRecording/Tabs/ClipsTab";
import NotesTab from "../AiRecording/Tabs/NotesTab";
import {
  AskPanel,
  introMessageForInterviews,
  suggestedInterviewMessages,
} from "../Candidate/Ask/AskPanel";
import { MediaPlayerInterface } from "../MediaPlayer/useMediaPlayer";
import { useAsk2Enabled } from "../Recording/SidebarTabs/Ai/Ask/useAsk2Enabled";
import { InterviewTab } from "./utils";

type AiInterviewSidebarProps = {
  currentTab?: InterviewTab;
  onTabChange?(t: InterviewTab): void;
  call: Pick<
    AiInterviewCallFragment,
    | "id"
    | "duration"
    | "canEdit"
    | "canShare"
    | "canShareExternal"
    | "clipsCount"
    | "position"
    | "speakers"
    | "transcriptLanguageCode"
  >;
  notes: CallNoteFragment[];
  notesLoading: boolean;
  notesError?: ApolloError;
  player: MediaPlayerInterface;
  onSeek(t: number): void;
  onCreateClipClick?(): void;
  notesForCurrentUserOnly: boolean;
  callId: string;
};

const AiInterviewSidebar: React.FC<AiInterviewSidebarProps> = ({
  currentTab,
  onTabChange,
  call,
  notes,
  notesLoading,
  notesError,
  player,
  onSeek,
  onCreateClipClick,
  notesForCurrentUserOnly,
  callId,
}) => {
  const sendGAEvent = useSendGAEvent();
  const ask2Enabled = useAsk2Enabled();

  return (
    <SidebarTabs
      id="call-sidebar"
      variant="underlined"
      currentTab={currentTab}
      onTabChange={(newTab) => {
        if (newTab !== currentTab) {
          sendGAEvent("sidebar_tab_change", "call_review", newTab);
        }
        onTabChange?.(newTab);
      }}
    >
      {ask2Enabled && (
        <SidebarTab
          id={InterviewTab.ASSIST}
          selectedColor="purple.600"
          displayName={
            <Flex alignItems="center" textColor="purple.600">
              <Icon
                as={HiOutlineSparkles}
                color="purple.600"
                h="16px"
                mr="3px"
                w="16px"
              />
              Assistant
            </Flex>
          }
        >
          <AskPanel
            requestPath={`/interview/${callId}/ask2`}
            onClickTimestamp={onSeek}
            onClose={() => {
              // No-op for inline mode
            }}
            hidden={false}
            inline
            suggestedMessages={suggestedInterviewMessages}
            introMessage={introMessageForInterviews}
            eventCategory="call_review"
          />
        </SidebarTab>
      )}
      <SidebarTab id={InterviewTab.NOTES} displayName="User notes" padBottom>
        <NotesTab
          call={call}
          notes={notes}
          loading={notesLoading}
          error={notesError}
          onClickTimestamp={onSeek}
          playerTime={player.time}
          notesForCurrentUserOnly={notesForCurrentUserOnly}
          overflow="hidden"
        />
      </SidebarTab>
      {onCreateClipClick && (
        <SidebarTab
          id={InterviewTab.CLIPS}
          displayName={`Clips${
            call.clipsCount > 0 ? ` (${call.clipsCount})` : ""
          }`}
        >
          <ClipsTab call={call} onCreateClipClick={onCreateClipClick} />
        </SidebarTab>
      )}
    </SidebarTabs>
  );
};

export default AiInterviewSidebar;
